import React, { useEffect } from 'react';
import { createPortal } from 'react-dom';
import { useDispatch } from 'react-redux';
import { AiManagementBoardTask } from 'store/modules/ai_management_store/types';
import { getTasks } from 'store/modules/ai_management_store/actions';
import { BiddingTaskTypes } from 'store/modules/bidding_store/types';
import { deleteTask } from 'services/actions/ai_management_tasks';
import { useTaskDetail } from 'hooks/useTaskDetail';
import Loader from 'components/atoms/loader';
import AiManagementTaskDetail from 'components/molecules/ai_management_task_detail';
import { Backdrop, Panel } from './styles';

interface AiManagementTaskDrawerProps {
  task: AiManagementBoardTask;
  onClose: () => void;
}

const AiManagementTaskDrawer: React.FC<AiManagementTaskDrawerProps> = ({ task, onClose }) => {
  const dispatch = useDispatch();
  const { task: detail, loading, error, replace } = useTaskDetail(task.id);
  const { bidding } = task;

  const handleSaved = (updated: BiddingTaskTypes) => {
    replace(updated);
    dispatch(getTasks());
  };

  const handleDelete = () => {
    if (!window.confirm(`Excluir a tarefa "${task.title}"?`)) return;
    deleteTask(task.id).then((res) => {
      if (!res || res.status >= 400) return;
      dispatch(getTasks());
      onClose();
    });
  };

  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', handleKeyDown);
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.removeEventListener('keydown', handleKeyDown);
      document.body.style.overflow = previousOverflow;
    };
  }, [onClose]);

  return createPortal(
    <>
      <Backdrop onClick={onClose} />
      <Panel>
        <div className="task-drawer-header d-flex align-items-start justify-content-between px-4 pt-4 pb-3 border-bottom">
          <div className="min-width-0">
            <h4 className="mb-1">Tarefa</h4>
            <div className="text-muted text-truncate">
              {bidding.notice_number} · {bidding.public_body}
            </div>
          </div>
          <div className="d-flex align-items-center">
            <button
              type="button"
              className="task-drawer-delete btn btn-link p-0 d-flex align-items-center mr-3"
              aria-label="Excluir tarefa"
              onClick={handleDelete}
            >
              <i className="material-icons">delete_outline</i>
            </button>
            <button
              type="button"
              className="btn btn-link text-dark p-0 d-flex align-items-center"
              aria-label="Fechar"
              onClick={onClose}
            >
              <i className="material-icons">close</i>
            </button>
          </div>
        </div>

        {loading && !detail && (
          <div className="py-5">
            <Loader />
          </div>
        )}

        {error && !detail && (
          <div className="alert alert-warning m-4" role="alert">
            Não foi possível carregar a tarefa.
          </div>
        )}

        {(detail || !loading) && !error && (
          <AiManagementTaskDetail task={detail ?? task} onSaved={handleSaved} />
        )}
      </Panel>
    </>,
    document.body,
  );
};

export default AiManagementTaskDrawer;
